import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { Checkbox } from '@components/ui/checkbox';
import { FormField, FormItem, FormControl } from '@components/ui/form';
// import { Textarea } from '@components/ui/textarea';
import { HealthOption } from '@features/onboarding/components/6-health-question-form/interfaces';
import { useHealthStore } from 'src/store/health';
import { FloatingInput } from '@components/ui/input-floating';
import { UseFormReturn, Path } from 'react-hook-form';
import { FormMessageSpan } from '@components/ui/form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import {
  CONDITION_DESCRIPTION_ERROR,
  HIDE_DETAILS_FOR,
} from '@features/onboarding/components/6-health-question-form/constants';
import { useStepBlockContext } from '@features/onboarding/layouts/OnboardingFormStepLayout';

interface FieldValue {
  value: string;
  groupKey?: string;
  details?: string;
}

interface FlatCheckboxGroupProps {
  options: (string | HealthOption)[];
  groupedOptions: HealthOption[][];
  fields: FieldValue[];
  append: (value: FieldValue) => void;
  remove: (index?: number) => void;
  replace: (value: FieldValue[]) => void;
  questionId: string;
  errorMessage?: string | null;
  selectedOptions: string[];
  noneValue: string | undefined;
  isNoneSelected: boolean;
  visibleOptions: string[];
  form: UseFormReturn<HealthQuestionsSchema>;
  description?: string;
  setStepErrorMessage: (msg: string | null) => void;
}

export const OtherHealthConditions: React.FC<FlatCheckboxGroupProps> = ({
  options,
  groupedOptions,
  fields,
  append,
  remove,
  replace,
  questionId,
  errorMessage,
  selectedOptions,
  noneValue,
  isNoneSelected,
  visibleOptions,
  form,
  description,
  setStepErrorMessage
}) => {
  const isStepBlocked = useStepBlockContext();
  const { issues, issueDetails, isFailValidation, setFailValidation } = useHealthStore();
  const prevSelectedOptions = useRef<string[]>(selectedOptions);
  const detailsRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  // Sort fields by their corresponding labels
  const sortedFields = React.useMemo(() => {
    return [...fields].sort((a, b) => {
      const optionA = options.find((opt) => 
        typeof opt !== 'string' && (opt as HealthOption).value === a.value
      ) as HealthOption;
      const optionB = options.find((opt) => 
        typeof opt !== 'string' && (opt as HealthOption).value === b.value
      ) as HealthOption;
      
      if (!optionA || !optionB) return 0;
      
      return optionA.label.localeCompare(optionB.label);
    });
  }, [fields, options]);

  // Easing function for smooth scrolling
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Smooth scroll function
  const smoothScrollTo = useCallback((element: HTMLElement, duration: number = 800) => {
    // Check screen width to decide scrolling method
    const isLargeScreen = window.innerWidth >= 1400;
    const scrollContainer = isLargeScreen ? document.getElementById('childrenBlock') : null;
    
    const getScrollPosition = (elem: HTMLElement, container?: HTMLElement | null) => {
      let top = 0;
      let current = elem;
      while (current && (!container || current !== container)) {
        top += current.offsetTop;
        current = current.offsetParent as HTMLElement;
      }
      return top - (container ? container.offsetTop : 0) - (isLargeScreen ? 100 : 100);
    };
    
    const start = scrollContainer ? scrollContainer.scrollTop : window.pageYOffset;
    const target = getScrollPosition(element, scrollContainer);
    const distance = target - start;
    let startTime: number | null = null;
    
    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);
      
      if (scrollContainer) {
        scrollContainer.scrollTop = start + distance * easeProgress;
      } else {
        window.scrollTo(0, start + distance * easeProgress);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }, []);

  const visibleDetailOptions = options.filter((option) => {
    if (typeof option === 'string') return false;
    const optionValue = option.value;
    return (
      visibleOptions.includes(optionValue) &&
      !HIDE_DETAILS_FOR.includes(optionValue)
    );
  });

  useEffect(() => {
    if (sortedFields && sortedFields.length > 0) {
      setStepErrorMessage(null);
    }
  }, [sortedFields, setStepErrorMessage]);

  useEffect(() => {
    if (!isStepBlocked || !noneValue) {
      return;
    }

    const hasSelections = fields.length > 0 || selectedOptions.length > 0;
    if (hasSelections) {
      const hasNoneSelected = fields.some((field) => field.value === noneValue);
      if (hasNoneSelected && !isNoneSelected) {
        setFailValidation(false);
      }
      return;
    }

    setFailValidation(false);
    console.log('[OtherHealthConditions] Auto-selecting noneValue', {
      questionId,
      noneValue,
      isStepBlocked,
      fieldsLength: fields.length,
      selectedOptionsLength: selectedOptions.length,
    });
    append({ value: noneValue });
  }, [
    isStepBlocked,
    noneValue,
    fields,
    append,
    selectedOptions.length,
    questionId,
    setFailValidation,
    selectedOptions,
    isNoneSelected,
  ]);

  const clearLinkedMedDetails = useCallback((optionValues?: string[]) => {
    if (questionId !== 'surgeriesDiabetesHeartMental') {
      return;
    }

    const conditionsPath = 'healthQuestions.medDetails.medications' as const;
    const detailsPath = 'healthQuestions.medDetails.details' as const;

    if (!optionValues) {
      const currentConditions = form.getValues(conditionsPath) || [];
      const currentDetails = form.getValues(detailsPath) || {};

      if (currentConditions.length > 0) {
        form.setValue(conditionsPath, [], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      }

      if (Object.keys(currentDetails).length > 0) {
        form.setValue(detailsPath, {}, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      }
      return;
    }

    const valuesToRemove = new Set<string>();
    optionValues.forEach((optionValue) => {
      const issue = issues.find((item) => item.key === optionValue);
      if (!issue) {
        return;
      }

      issueDetails
        .filter((detail) => detail.issueId === issue.id)
        .forEach((detail) => {
          valuesToRemove.add(`${optionValue}-${detail.key}`);
        });
    });

    if (valuesToRemove.size === 0) {
      return;
    }

    const currentConditions:
      | Array<{ value: string; details?: string }>
      | undefined = form.getValues(conditionsPath);
    const nextConditions = (currentConditions || []).filter(
      (condition) => !valuesToRemove.has(condition.value)
    );

    if ((currentConditions || []).length !== nextConditions.length) {
      form.setValue(conditionsPath, nextConditions, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }

    const currentDetails: Record<string, string> =
      form.getValues(detailsPath) || {};
    const nextDetails = { ...currentDetails };
    let detailsChanged = false;
    valuesToRemove.forEach((value) => {
      if (Object.prototype.hasOwnProperty.call(nextDetails, value)) {
        delete nextDetails[value];
        detailsChanged = true;
      }
    });

    if (detailsChanged) {
      form.setValue(detailsPath, nextDetails, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }
  }, [form, issueDetails, issues, questionId]);

  useEffect(() => {
    if (questionId !== 'surgeriesDiabetesHeartMental') return;

    const prev = prevSelectedOptions.current;
    const added = selectedOptions.find((v) => !prev.includes(v));
    const removed = prev.find((v) => !selectedOptions.includes(v));
    const changedOption = added ?? removed;

    prevSelectedOptions.current = selectedOptions;

    if (selectedOptions.includes('none')) {
      clearLinkedMedDetails();
      return;
    }

    if (!changedOption) return;

    clearLinkedMedDetails([changedOption]);
  }, [questionId, selectedOptions, form, clearLinkedMedDetails]);

  // Track initial render to prevent scrolling on page load
  const isInitialRender = useRef(true);
  useEffect(() => {
    isInitialRender.current = false;
  }, []);
  
  // Do not use useEffect for scrolling to avoid unwanted scrolls
  // Scrolling is handled directly in onCheckedChange

  const detailsPath =
    `healthQuestions.${questionId}.details` as Path<HealthQuestionsSchema>;

  const clearDetails = (optionValues?: string[]) => {
    const currentDetails =
      form.getValues(detailsPath) as Record<string, string> | undefined;

    if (!currentDetails || Object.keys(currentDetails).length === 0) {
      return;
    }

    if (!optionValues) {
      form.unregister(detailsPath);
      form.setValue(detailsPath, {}, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      return;
    }

    const updatedDetails = { ...currentDetails };
    optionValues.forEach((value) => {
      form.unregister(`${detailsPath}.${value}` as Path<HealthQuestionsSchema>);
      delete updatedDetails[value];
    });
    form.setValue(detailsPath, updatedDetails, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  };

  const detailsErrors: Record<string, string> = {};
  selectedOptions.forEach((option) => {
    if (option !== noneValue && !HIDE_DETAILS_FOR.includes(option)) {
      const details = form.getValues(`healthQuestions.${questionId}.details.${option}` as Path<HealthQuestionsSchema>);
      if (!details || (details as string).trim() === '') {
        detailsErrors[option] = CONDITION_DESCRIPTION_ERROR;
      }
    }
  });

  // Auto-scroll to error message when no options are selected (including NONE APPLY)
  useEffect(() => {
    const nothingSelected = fields.length === 0 && selectedOptions.length === 0;
    if (errorMessage && nothingSelected && errorRef.current) {
      setTimeout(() => {
        smoothScrollTo(errorRef.current as HTMLElement);
      }, 100);
    }
  }, [errorMessage, fields.length, selectedOptions.length, smoothScrollTo]);

  return (
    <>
      <p className="text-[15px] lg:text-xl leading-6 lg:leading-7 mb-10 max-w-170">
        {description}
      </p>
      <div
        className={`${
          ['medDetails', 'otherHealthConditions'].includes(
            questionId
          )
            ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2'
            : 'flex flex-col gap-y-2'
        }`}
      >
        {options
          ?.filter((option) => typeof option !== 'string' && !(option as HealthOption).isNoneApply)
          .map((option, index) => {
            const optionLabel = (option as HealthOption).label;
            const optionValue = (option as HealthOption).value;
            const itemKey = optionValue || `opt-${index}`;
            const isChecked = fields.some(
              (field: FieldValue) => field.value === optionValue
            );
            return (
              <div key={itemKey} className="flex items-center">
                <Checkbox
                  id={`${questionId}-${optionValue}`}
                  label={optionLabel}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      const noneIndex = fields.findIndex(
                        (field: FieldValue) => field.value === noneValue
                      );
                      if (noneIndex !== -1) {
                        remove(noneIndex);
                      }
                      append({ value: optionValue });
                      
                      // Auto-scroll to details section only when checking a box (not unchecking)
                      if (!HIDE_DETAILS_FOR.includes(optionValue)) {
                        setTimeout(() => {
                          if (detailsRef.current) {
                            smoothScrollTo(detailsRef.current);
                          }
                        }, 300);
                      }
                    } else {
                      const indexToRemove = fields.findIndex(
                        (field: FieldValue) => field.value === optionValue
                      );
                      if (indexToRemove !== -1) {
                        remove(indexToRemove);
                      }
                      clearLinkedMedDetails([optionValue]);
                      clearDetails([optionValue]);
                    }
                  }}
                />
              </div>
            );
          })}
      </div>
      {/* NONE APPLY option below the grid */}
      {options
        ?.filter((option) => typeof option !== 'string' && (option as HealthOption).isNoneApply)
        .map((option, index) => {
          const optionLabel = (option as HealthOption).label;
          const optionValue = (option as HealthOption).value;
          const itemKey = optionValue || `none-${index}`;
          const isChecked = fields.some(
            (field: FieldValue) => field.value === optionValue
          );
          return (
            <div key={itemKey} className="flex items-center mt-12">
              <Checkbox
                id={`${questionId}-${optionValue}`}
                label={optionLabel}
                checked={isChecked}
                onCheckedChange={(checked) => {
                  if (checked) {
                    clearDetails();
                    clearLinkedMedDetails();
                    replace([{ value: optionValue }]);
                  } else {
                    const indexToRemove = fields.findIndex(
                      (field: FieldValue) => field.value === optionValue
                    );
                    if (indexToRemove !== -1) {
                      remove(indexToRemove);
                    }
                  }
                }}
              />
            </div>
          );
        })}
      {errorMessage && (
        <p ref={errorRef}>
          <FormMessageSpan
            className="text-error font-extrabold mt-2 text-left"
            text={errorMessage}
          />
        </p>
      )}

      {visibleDetailOptions.length > 0 && !isNoneSelected && (
        <div ref={detailsRef} className="mt-4 border-t pt-4 space-y-4">
          {visibleDetailOptions.map((option, index) => {
            const healthOption = option as HealthOption;
            const optionValue = healthOption.value;
            const itemKey = optionValue || `detail-${index}`;
            return (
              <div key={itemKey} className="flex flex-col gap-2">
                <label className="text-sm">
                  Please provide a description for:{' '}
                  <span className="font-bold">
                    {healthOption.label}
                  </span>
                </label>
                <FormField
                  control={form.control}
                  name={
                    `healthQuestions.${questionId}.details.${optionValue}` as Path<HealthQuestionsSchema>
                  }
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingInput
                          {...field}
                          as="textarea"
                          id={`details-${optionValue}`}
                          value={`${field?.value ?? ''}`}
                          aria-invalid={
                            isFailValidation &&
                            !!(fieldState.error || detailsErrors[optionValue])
                          }
                          inputClass="border rounded-lg p-2 w-full"
                          label="Description"
                          onChange={e => {
                            field.onChange(e);
                            setFailValidation(false);
                            if (detailsErrors[optionValue]) {
                              detailsErrors[optionValue] = '';
                            }
                          }}
                        />
                      </FormControl>
                      {(isFailValidation && (fieldState.error || detailsErrors[optionValue])) && (
                        <p ref={errorRef}>
                          <FormMessageSpan
                            className="text-error font-semibold mt-2"
                            text={fieldState.error?.message || detailsErrors[optionValue]}
                          />
                        </p>
                      )}
                    </FormItem>
                  )}
                />
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
